import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { generatePath } from 'react-router-dom';

import { SECTION } from 'src/constants/section';
import type {
  FilterGroup,
  FollowUpAction,
  FollowUpActionForm,
  MutationError,
} from 'src/types/myFollowUp';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import useFollowUpActionTable from 'src/components/myFollowUp/FollowUpActionPage/components/FollowUpActionTable/FollowUpActionTableContext';
import useKPIFilterGroupQuery from 'src/components/myFollowUp/hooks/useKPIFilterGroupQuery';
import useFollowUpTable from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpTable/FollowUpTableContext';
import {
  useActionItemDetailQuery,
  useActionItemEditQuery,
} from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/hooks';
import { useSurveyDetailQuery } from 'src/components/survey/SurveySettings/api';

interface FollowUpDetailValue {
  actionItem?: FollowUpAction;
  kpis?: FilterGroup;
  myFollowUpDeadline?: Date;
  onSave: (data?: Partial<FollowUpActionForm>) => Promise<void>;
  onDelete: (followUpActionId: string) => void;
  savedSuccessfully: boolean;
  resetAfterSave: () => void;
  deletedSuccessfully: boolean;
  deleteError: unknown | null;
  resetAfterDelete: () => void;
  updateFormData: (data: Partial<FollowUpActionForm>) => void;
  setActionItemBeingCreated: (data: Partial<FollowUpAction>) => void;
  resetFormData: () => void;
  loading: boolean;
  actionItemError: unknown | null;
  kpisError: unknown | null;
  mutationError: MutationError | null;
  disabled?: boolean;
}

const FollowUpDetailContext = createContext<FollowUpDetailValue>({
  onSave: () => Promise.resolve(),
  onDelete: () => Promise.resolve(),
  savedSuccessfully: false,
  resetAfterSave: () => {},
  deletedSuccessfully: false,
  deleteError: null,
  resetAfterDelete: () => {},
  updateFormData: () => {},
  resetFormData: () => {},
  setActionItemBeingCreated: () => {},
  loading: false,
  actionItemError: null,
  kpisError: null,
  mutationError: null,
  disabled: false,
});

interface FollowUpDetailProviderProps {
  actionId: number | null;
  children?: ReactNode;
  enable: boolean;
  onSuccessfulCreation?: (newActionItemId: number) => void;
}

export const FollowUpDetailProvider: FC<FollowUpDetailProviderProps> = ({
  actionId,
  children,
  enable,
  onSuccessfulCreation,
}) => {
  const [actionItemBeingCreated, setActionItemBeingCreated] =
    useState<Partial<FollowUpAction>>();
  const {
    hasReadOnlyPermissionForModuleType,
    hasAdminPermissionForModuleType,
  } = useUserPermissions();
  const [deletedSuccessfully, setDeletedSuccessfully] = useState(false);
  const [deleteError, setDeleteError] = useState<unknown | null>(null);
  const resetAfterDelete = () => {
    setDeletedSuccessfully(false);
    setDeleteError(null);
  };
  const {
    actionItem,
    isFetching: isFetchingActionItem,
    error: actionItemError,
  } = useActionItemDetailQuery(
    enable ? actionId : null,
    actionItemBeingCreated,
  );
  const [loading, setLoading] = useState(false);

  const {
    data: kpis,
    isFetching: isFetchingKpis,
    error: kpisError,
  } = useKPIFilterGroupQuery(
    actionItem?.surveyId,
    actionItem?.orgUnits
      ?.map((nestedOrgUnit) => {
        const orgUnit = nestedOrgUnit[nestedOrgUnit.length - 1];
        // We have 2 differently named orgUnitId properties for the orgUnit here: From the FollowUpAction and from the
        // orgUnit search for creating a follow up action. todo: Needs to be refactored in #1406
        const { org_unit_id, orgUnitId } = orgUnit;
        return orgUnitId ?? org_unit_id;
      })
      .filter(Boolean),
  );

  const { data: survey } = useSurveyDetailQuery(actionItem?.surveyId);

  const myFollowUpDeadline = useMemo(
    () =>
      survey?.myFollowUpDeadline
        ? new Date(survey.myFollowUpDeadline)
        : undefined,
    [survey],
  );

  const {
    mutateAsync,
    error: mutationError,
    isSuccess: savedSuccessfully,
    reset: resetAfterSave,
  } = useActionItemEditQuery(enable ? actionId : null);
  const { refetch: refetchMyFollowUpList } = useFollowUpTable();
  const { refetch: refetchFollowUpActionList } = useFollowUpActionTable();
  const [formData, setFormData] = useState<Partial<FollowUpActionForm>>({});
  const disabled =
    hasReadOnlyPermissionForModuleType(
      SECTION.FOLLOW_UP,
      actionItem?.surveyId,
    ) && !hasAdminPermissionForModuleType(SECTION.FOLLOW_UP);

  useEffect(() => {
    // If actionItemBeingCreated is set, we use it to initialize formData
    if (!actionId && actionItemBeingCreated) {
      setFormData((prevData) => ({
        ...prevData,
        ...actionItemBeingCreated,
        orgUnits: actionItemBeingCreated.orgUnits?.map(
          (nestedOrgUnit) => nestedOrgUnit[nestedOrgUnit.length - 1].id,
        ),
      }));
    }
  }, [actionItemBeingCreated, actionId]);

  const resetFormData = useCallback(() => setFormData({}), []);

  const updateFormData = useCallback((data: Partial<FollowUpActionForm>) => {
    setFormData((prevData) => ({ ...prevData, ...data }));
  }, []);

  const onSave = (data?: Partial<FollowUpActionForm>) =>
    mutateAsync(data ? data : formData).then((createdOrUpdatedItem) => {
      void setActionItemBeingCreated(undefined);
      if (!actionId && createdOrUpdatedItem?.id) {
        onSuccessfulCreation?.(createdOrUpdatedItem.id);
      }

      void refetchMyFollowUpList();
      void refetchFollowUpActionList();
    });

  const onDelete = useCallback(
    async (followUpActionId: string) => {
      setLoading(true);
      try {
        await HttpServiceInstance.delete(
          generatePath(apiPaths.FOLLOW_UP_ACTIONS.DETAIL, {
            actionItemId: followUpActionId,
          }),
          {},
        );
        setDeletedSuccessfully(true);
        void refetchMyFollowUpList();
        void refetchFollowUpActionList();
      } catch (error) {
        setDeleteError(error);
      } finally {
        setLoading(false);
      }
    },
    [
      HttpServiceInstance,
      setDeletedSuccessfully,
      refetchMyFollowUpList,
      refetchFollowUpActionList,
    ],
  );

  return (
    <FollowUpDetailContext.Provider
      value={{
        actionItem,
        kpis,
        myFollowUpDeadline,
        onSave,
        onDelete,
        savedSuccessfully,
        deletedSuccessfully,
        deleteError,
        resetAfterDelete,
        resetAfterSave,
        updateFormData,
        resetFormData,
        setActionItemBeingCreated,
        loading:
          (!!actionId && (isFetchingActionItem || isFetchingKpis)) || loading,
        actionItemError,
        kpisError,
        mutationError,
        disabled,
      }}
    >
      {children}
    </FollowUpDetailContext.Provider>
  );
};

const useFollowUpDetail = () => useContext(FollowUpDetailContext);
export default useFollowUpDetail;
